import React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Link,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (section: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const quickLinks = [
    { label: 'Home', section: 'home' },
    { label: 'About Us', section: 'about' },
    { label: 'Services', section: 'services' },
    { label: 'Blog', section: 'blog' },
    { label: 'Contact', section: 'contact' },
  ];

  const services = [
    { label: 'Virtualization Solutions', slug: 'virtualization' },
    { label: 'Private Cloud & Compute', slug: 'private-cloud' },
    { label: 'Disaster Recovery', slug: 'disaster-recovery' },
    { label: 'Data Backup & Protection', slug: 'data-backup' },
  ];

  const certifications = [
    'Enterprise Certified Partner',
    'ISO 9001:2015 Certified',
    'Industry Expert Team',
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'primary.main',
        color: 'white',
        py: 6,
        mt: 8,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {/* Company Information */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CloudIcon sx={{ mr: 1, fontSize: 32 }} />
              <Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                  TNCOSOFT
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  TNCOSOFT Consultancy Pvt Ltd
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
              Professional IT infrastructure services delivering modern virtualization, cloud computing, 
              and data protection solutions to businesses worldwide.
            </Typography>
            
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
                <Typography variant="body2">+91-8999096807</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, fontSize: 18 }} />
                <Typography variant="body2">njinabad@tncosoft.com</Typography>
              </Box>

            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={6} md={2}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, mb: { xs: 1.5, md: 2 } }}>
              Quick Links
            </Typography>
            <Stack spacing={{ xs: 0.5, md: 1 }}>
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  component="button"
                  onClick={() => handleNavigation(link.section)}
                  color="inherit"
                  underline="hover"
                  sx={{ 
                    opacity: 0.9, 
                    '&:hover': { opacity: 1 },
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: { xs: '0.875rem', md: 'inherit' },
                    fontFamily: 'inherit'
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Services */}
          <Grid item xs={6} sm={6} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, mb: { xs: 1.5, md: 2 } }}>
              Services
            </Typography>
            <Stack spacing={{ xs: 0.5, md: 1 }}>
              {services.map((service) => (
                <Link
                  key={service.label}
                  component="button"
                  onClick={() => {
                    navigate(`/services/${service.slug}`);
                    window.scrollTo(0, 0);
                  }}
                  color="inherit"
                  underline="hover"
                  sx={{ 
                    opacity: 0.9, 
                    '&:hover': { opacity: 1 },
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: { xs: '0.875rem', md: 'inherit' },
                    fontFamily: 'inherit',
                    lineHeight: { xs: 1.3, md: 1.5 }
                  }}
                >
                  {service.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Certifications */}
          <Grid item xs={12} md={3}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' }, mb: { xs: 1.5, md: 2 } }}>
              Certifications
            </Typography>
            <Stack spacing={1}>
              {certifications.map((cert, index) => (
                <Chip
                  key={index}
                  label={cert}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    alignSelf: 'flex-start',
                  }}
                />
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

        {/* Bottom Section */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © 2024-2026 TNCOSOFT. All rights reserved.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Stack direction="row" spacing={3}>
                <Link
                  component={RouterLink}
                  to="/privacy-policy"
                  color="inherit"
                  underline="hover"
                  sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                >
                  Privacy Policy
                </Link>
                <Link
                  component={RouterLink}
                  to="/terms-of-service"
                  color="inherit"
                  underline="hover"
                  sx={{ opacity: 0.8, '&:hover': { opacity: 1 } }}
                >
                  Terms of Service
                </Link>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* ISO Certification */}
        <Box sx={{ 
          mt: 3,
          textAlign: 'center'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 1.5,
            bgcolor: 'rgba(255,255,255,0.1)',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            mx: 'auto',
            width: 'fit-content'
          }}>
            <VerifiedIcon sx={{ fontSize: '1.2rem', color: '#4caf50' }} />
            <Typography variant="body2" sx={{ 
              fontSize: { xs: '0.85rem', md: '0.95rem' },
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}>
              ISO 9001:2015 Certified Company
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;